import { useEffect, useRef, useState } from 'react'
import { Dialog, Box, IconButton, Chip } from '@mui/material'
import { Close, Timer, Visibility } from '@mui/icons-material'

interface EphemeralImageDialogProps {
  uri: string
  /** Remaining views for this viewer; null = uncounted (the sender viewing their own). */
  remaining: number | null
  /** Max seconds to keep the image open before auto-closing (null/undefined = no time limit). */
  seconds?: number | null
  onClose: () => void
}

/**
 * Full-screen viewer for a view-limited (ephemeral) image on web. Auto-closes when the optional
 * max-view-time countdown runs out so the image can't be left open. (Browsers can't block
 * screenshots — full-screen + time limit is the strongest the web platform allows.)
 * Rendered only while open (the parent mounts/unmounts it).
 */
export function EphemeralImageDialog({ uri, remaining, seconds, onClose }: EphemeralImageDialogProps) {
  const [countdown, setCountdown] = useState<number | null>(seconds ?? null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!seconds) return
    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c == null) return c
        if (c <= 1) {
          clearInterval(interval)
          onCloseRef.current()
          return 0
        }
        return c - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [seconds])

  return (
    <Dialog fullScreen open onClose={onClose} PaperProps={{ sx: { bgcolor: '#000' } }}>
      <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src={uri} alt="Chat image" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        <Box sx={{ position: 'absolute', top: 12, left: 12, right: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <IconButton onClick={onClose} sx={{ color: '#fff' }} aria-label="Close image">
            <Close />
          </IconButton>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {countdown != null && (
              <Chip
                icon={<Timer sx={{ fontSize: 16, color: '#fff !important' }} />}
                label={`${countdown}s`}
                sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: '#fff' }}
              />
            )}
            {remaining != null && (
              <Chip
                icon={<Visibility sx={{ fontSize: 16, color: '#fff !important' }} />}
                label={remaining > 0 ? `${remaining} view${remaining === 1 ? '' : 's'} left` : 'Last view'}
                sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: '#fff' }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  )
}
